export type FormElementStatus = "valid" | "invalid" | "empty" | "";

export interface FormElement {
  value: string;
  status: FormElementStatus;
  message: string;
  details?: string[];
}

export type FormElements = Record<string, FormElement>;

const validatePassword = (password: string): string[] => {
  const digitRegex = /\d/;
  const lowerCaseRegex = /[a-z]/;
  const upperCaseRegex = /[A-Z]/;
  const specialRegex = /[^A-Za-z0-9]/;
  const lengthRegex = /.{10,}/;

  const errors: string[] = [];
  !digitRegex.test(password) && errors.push("1 digit");
  !lowerCaseRegex.test(password) && errors.push("1 lowercase letter");
  !upperCaseRegex.test(password) && errors.push("1 uppercase letter");
  !specialRegex.test(password) && errors.push("1 special character");
  !lengthRegex.test(password) && errors.push("10 or more characters");

  return errors;
};

export const validateFormElement = (field: string, value: string): FormElement => {
  if (value === "") {
    return { value, status: "invalid", message: "This field is required." };
  }

  if (field === "email") {
    if (!/\S+@\S+\.\S+/.test(value)) {
      return { value, status: "invalid", message: "Please enter a valid email address." };
    }
  }

  if (field === "password") {
    const pwErrors = validatePassword(value);

    if (pwErrors.length > 0) {
      return {
        value,
        status: "invalid",
        message: "Password is of insufficient strength. Please ensure it meets all requirements.",
        details: pwErrors,
      };
    }
  }

  return { value, status: "valid", message: "" };
};

/** Validate entire form by running each element through validateFormElement. */
export const validateForm = (formElements: FormElements): FormElements => {
  const validatedElements: FormElements = {};
  Object.keys(formElements).forEach(key => {
    validatedElements[key] = validateFormElement(key, formElements[key].value);
  });

  return validatedElements;
};

/** Check if any form element has an invalid status. */
export const formHasErrors = (formElements: FormElements): boolean => {
  return Object.values(formElements).some(e => e.status === "invalid");
};

/** Maps form field names to their corresponding Cognito user attribute names. */
const COGNITO_ATTRIBUTE_MAP: Record<string, string> = {
  givenName: "name",
  familyName: "family_name",
  company: "custom:company",
  jobRole: "custom:job_role",
  title: "custom:title",
};

/** Maps form field names to backend RegisterUserAttributes names. */
const BACKEND_REGISTRATION_ATTRIBUTE_MAP: Record<string, string> = {
  givenName: "givenName",
  familyName: "familyName",
  company: "company",
  jobRole: "jobRole",
  title: "title",
};

/** Convert form elements to Cognito user attributes, mapping field names to their Cognito attribute names and omitting email and password. */
export const formElementsToAttributes = (formElements: FormElements): Record<string, string> => {
  const attributes: Record<string, string> = {};
  Object.keys(formElements).forEach(key => {
    if (key === "email" || key === "password") return;
    const cognitoName = COGNITO_ATTRIBUTE_MAP[key];
    if (cognitoName) {
      attributes[cognitoName] = formElements[key].value;
    }
  });

  return attributes;
};

/** Convert form elements to backend registration attributes, mapping field names to the RegisterUserAttributes keys and omitting email and password. */
export const formElementsToBackendRegistrationAttributes = (
  formElements: FormElements
): Record<string, string> => {
  const attributes: Record<string, string> = {};
  Object.keys(formElements).forEach(key => {
    if (key === "email" || key === "password") return;
    const backendName = BACKEND_REGISTRATION_ATTRIBUTE_MAP[key];
    if (backendName) {
      attributes[backendName] = formElements[key].value;
    }
  });

  return attributes;
};
